import type { ItemFieldRow } from './item-field-reader';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { phpJsonTruthy } from './php-json-values';

export class MissingLayerEntryDefinition extends Error {
    constructor(public readonly id: string) {
        super(`Missing ft_resolve_layer_entry definition result: ${id}`);
        this.name = 'MissingLayerEntryDefinition';
    }
}

function arrayLike(value: unknown): value is ItemFieldRow {
    return value !== null && typeof value === 'object';
}
function own(value: unknown, key: string): unknown {
    return arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key)
        ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
}
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index))
        ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
function fullDefinition(value: unknown): value is ItemFieldRow {
    return arrayLike(value) && own(own(value, 'items'), 'item_id') != null && phpJsonTruthy(own(value, 'item_layer_types'));
}
const upperAscii = (value: string): string => value.replace(/[a-z]/g, letter => letter.toUpperCase());
const lowerAscii = (value: string): string => value.replace(/[A-Z]/g, letter => letter.toLowerCase());

/** ft_layer_entry_needs_resolve is a gate, not equivalent to whether resolution can return a definition. */
export function layerEntryNeedsResolve(value: unknown): boolean {
    if (typeof value === 'string') return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
    return arrayLike(value) && !phpJsonTruthy(own(value, 'item_layer_types'));
}

function catalogDefinition(root: ItemFieldRow, id: string): ItemFieldRow | null {
    const catalog = own(root, 'catalog');
    if (id === '' || !arrayLike(catalog)) return null;
    const entry = own(catalog, id) ?? own(catalog, upperAscii(id)) ?? own(catalog, lowerAscii(id));
    return fullDefinition(entry) ? entry : null;
}

export function readCatalogDefinition(root: ItemFieldRow, id: string): ItemFieldRow | null {
    return clone(catalogDefinition(root, id)) as ItemFieldRow | null;
}

/** Complete ft_resolve_layer_entry selection. queryDefinition supplies a previously
 * resolved query_full_item_arr result; it neither fetches nor authorizes a fetch here.
 * A known null retains the original entry, while an unavailable result throws.
 * Read-only consumers may borrow the held definition instead of detaching it.
 */
export function resolveLayerEntry(entry: unknown, root: ItemFieldRow,
    queryDefinition?: (id: string) => ItemFieldRow | null, borrow = false): unknown {
    const view = borrow ? (value: unknown): unknown => value : clone;
    if (fullDefinition(entry)) return view(entry);
    const id = readLayerListEntryUuid(entry);
    if (id === '') return view(entry);
    const definition = catalogDefinition(root, id);
    if (definition !== null) return view(definition);
    if (!queryDefinition) throw new MissingLayerEntryDefinition(id);
    const cold = queryDefinition(id);
    return view(arrayLike(cold) ? cold : entry);
}
